import { addDays } from 'date-fns';
import React from 'react';

import { AddArchiveJobParameters } from '../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { formattedDate } from '../../../../utils/dateUtils';
import { publishNotification } from '../../../../utils/notifications/publish-notification';
import { RequestParamsToApiPaths } from '../../../../utils/typescript-utils';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { DateTimePickerDialog } from '../../date-time-picker-dialog/date-time-picker-dialog';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';

enum Status {
  Idle,
  Pending,
  Error,
}

interface Props {
  onClose: (archived: boolean) => void;
}

const ArchiveProjectDialog = (props: Props) => {
  const earliestArchiveDate = addDays(new Date(), 7);

  const { project } = React.useContext(ProjectContext);
  const [status, setStatus] = React.useState(Status.Idle);
  const { handleError, buildErrorList, includeErrorPaths } =
    useErrorHandling<RequestParamsToApiPaths<AddArchiveJobParameters>>();
  const [archiveDate, setArchiveDate] = React.useState<Date>(earliestArchiveDate);
  const dateTimePickerDialog = useDialog();

  const archive = async () => {
    try {
      setStatus(Status.Pending);

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      await projectService.addArchiveJob({
        path: { projectId: project.id },
        body: { archiveDate: formattedDate(archiveDate) },
      });

      publishNotification('succ0309');
      props.onClose(true);
    } catch (err: any) {
      setStatus(Status.Error);
      handleError(err);
    }
  };

  const cancel = () => {
    props.onClose(false);
  };

  return (
    <>
      <Dialog
        closeIcon={status === Status.Idle}
        closeOnDimmerClick={status === Status.Idle}
        useGoogleTranslate={true}
        width={500}
        onClose={cancel}
      >
        {{
          header: 'Arkivera projekt',
          content: (
            <Stack spacing={1}>
              <span>Vill du arkivera projektet?</span>

              <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'info'] }}>
                Projektet arkiveras automatiskt vid nedanstående datum.
                <br />
                <br />
                Återställning av ett arkiverat projekt tar några dagar. Fram till arkiveringsdatumet
                kan projektet återställas utan väntetid.
              </InfoBox>

              {buildErrorList({
                filter: includeErrorPaths(['path.projectId']),
                infoBoxProps: {
                  topMargin: false,
                },
              })}

              <LabelledContainer text="Arkiveringsdatum">
                {(formElementId) => (
                  <TextBox
                    id={formElementId()}
                    readOnly={true}
                    readOnlyCursor="pointer"
                    value={formattedDate(archiveDate)}
                    onClick={(event) => {
                      event.stopPropagation();
                      dateTimePickerDialog.show();
                    }}
                  />
                )}
              </LabelledContainer>
            </Stack>
          ),
          footer: {
            right: (
              <>
                <Button
                  color="error"
                  disabled={status === Status.Pending}
                  loading={status === Status.Pending}
                  variant="contained"
                  onClick={archive}
                >
                  Arkivera
                </Button>
                <Button disabled={status === Status.Pending} variant="text" onClick={cancel}>
                  Stäng
                </Button>
              </>
            ),
          },
        }}
      </Dialog>

      {dateTimePickerDialog.render(
        <DateTimePickerDialog
          displayTime={false}
          minDate={earliestArchiveDate}
          startDate={archiveDate}
          onCancel={() => {
            dateTimePickerDialog.hide();
          }}
          onDateChoosen={(date) => {
            setArchiveDate(date);
            dateTimePickerDialog.hide();
          }}
        />,
      )}
    </>
  );
};

export { ArchiveProjectDialog, Props as ArchiveProjectDialogProps };
